import { useEffect, useMemo, useState } from 'react';
import { Upload, Loader2, CheckSquare, XSquare, Search, Ban } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { supabase, supabaseAdmin } from '../../config/supabase';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from '../../components/ui/use-toast';
import slugify from '../../utils/slugify';
import { triggerKeywordGeneration } from '../../services/keywordService';

type BusinessTypeRow = { id: string; name: string };
type CategoryRow = { id: number; name: string; category?: string | null };

type GoogleMapsEntry = {
  title?: string;
  categoryName?: string;
  categories?: string[];
  address?: string;
  neighborhood?: string | null;
  street?: string | null;
  city?: string | null;
  postalCode?: string | null;
  state?: string | null;
  countryCode?: string | null;
  phone?: string | null;
  phoneUnformatted?: string | null;
  website?: string | null;
  imageUrl?: string | null;
  url?: string | null;
  totalScore?: number | null;
  reviewsCount?: number | null;
  placeId?: string | null;
};

// Internal UI model used by the review screen. Each candidate maps to exactly one row
// that can be imported as a mockup/unclaimed business into one of the business tables.
type ImportCandidate = {
  key: string;
  selected: boolean;
  source: GoogleMapsEntry;

  business_type_id: string;
  trade_category: string;
  profession_id: number | null;

  store_name: string;
  owner_name: string;
  email: string;
  phone_number: string;
  location: string;
  website: string;
  description: string;
  avatar_url: string;
};

type ImportStatus = {
  state: 'ok' | 'skipped' | 'failed';
  message: string;
};

type DuplicateHit = { table: string; id: string | number; reason: string };

const BUSINESS_TABLES = ['traders', 'crafties', 'hospitality', 'professionals'] as const;

const NON_UNIQUE_WEBSITE_DOMAINS = new Set([
  'facebook.com',
  'instagram.com',
  'linkedin.com',
  'tiktok.com',
  'twitter.com',
  'x.com',
  'google.com',
  'maps.google.com',
  'goo.gl',
  'maps.app.goo.gl',
  'g.page',
]);

// Supabase constraint errors may surface in slightly different shapes, so we detect
// them defensively. We use this to retry slug generation when there's a collision.
const isUniqueConstraintError = (err: unknown): boolean => {
  const anyErr = err as any;
  const code = anyErr?.code;
  const message = String(anyErr?.message ?? '');
  const details = String(anyErr?.details ?? '');
  const constraint = String(anyErr?.constraint ?? '');
  const haystack = `${message} ${details} ${constraint}`.toLowerCase();

  return (
    (code === '23505' || haystack.includes('duplicate key') || haystack.includes('unique constraint')) &&
    haystack.includes('slug')
  );
};

// Utility to avoid UI hanging forever if a request stalls.
const withTimeout = async <T,>(
  promise: PromiseLike<T>,
  timeoutMs: number,
  label: string
): Promise<T> => {
  let timeoutId: number | undefined;
  const timeoutPromise = new Promise<T>((_resolve, reject) => {
    timeoutId = window.setTimeout(() => {
      reject(new Error(`${label} timed out after ${Math.round(timeoutMs / 1000)}s`));
    }, timeoutMs);
  });

  try {
    return await Promise.race([Promise.resolve(promise), timeoutPromise]);
  } finally {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
  }
};

// Inserts a row and automatically retries if `slug` collides with an existing record.
const insertWithSlugRetry = async <T,>(
  tableName: string,
  insertBase: Record<string, any>,
  slugBase: string,
  timeoutMs: number
): Promise<T> => {
  const base = slugBase || 'business';
  const maxAttempts = 25;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const candidate = attempt === 0 ? base : `${base}-${attempt + 1}`;
    const insertData = {
      ...insertBase,
      slug: candidate,
    };
    try {
      const insertResult = await withTimeout<any>(
        supabase
          .from(tableName)
          .insert([insertData])
          .select()
          .single(),
        timeoutMs,
        `Database insert (${tableName})`
      );

      const { data: inserted, error } = insertResult;

      if (error) {
        throw error;
      }

      if (!inserted) {
        throw new Error('No data returned after insert');
      }

      return inserted as T;
    } catch (err) {
      if (isUniqueConstraintError(err)) {
        continue;
      }
      throw err;
    }
  }

  const fallbackCandidate = `${base}-${Date.now()}`;
  const finalResult = await withTimeout<any>(
    supabase
      .from(tableName)
      .insert([
        {
          ...insertBase,
          slug: fallbackCandidate,
        },
      ])
      .select()
      .single(),
    timeoutMs,
    `Database insert (${tableName})`
  );

  const { data: finalInserted, error: finalError } = finalResult;
  if (finalError) {
    throw finalError;
  }
  if (!finalInserted) {
    throw new Error('No data returned after insert');
  }

  return finalInserted as T;
};

const normalizeLocation = (entry: GoogleMapsEntry): string => {
  const address = entry.address?.trim();
  if (address) return address;

  const city = entry.city?.trim();
  const state = entry.state?.trim();

  if (city && state) return `${city}, ${state}`;
  if (city) return city;
  if (entry.neighborhood?.trim() && state) return `${entry.neighborhood.trim()}, ${state}`;
  if (state) return state;

  return '';
};

const buildDefaultDescription = (entry: GoogleMapsEntry): string => {
  const bits: string[] = [];

  const name = (entry.title || '').trim();
  if (name) bits.push(name);

  const primaryCategory = (entry.categoryName || entry.categories?.[0] || '').trim();
  if (primaryCategory) bits.push(primaryCategory);

  const address = entry.address?.trim();
  if (address) bits.push(address);

  return bits.join('\n');
};

const normalizeMatchKey = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^a-z0-9 ]/g, '');

const getEntryPrimaryCategory = (entry: GoogleMapsEntry): string =>
  (entry.categoryName || entry.categories?.[0] || '').trim();

const getCandidateRawCategory = (candidate: Pick<ImportCandidate, 'source' | 'description' | 'store_name'>): string => {
  const fromSource = getEntryPrimaryCategory(candidate.source);
  if (fromSource) return fromSource;

  const lines = (candidate.description || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length === 0) return '';

  const first = lines[0];
  const second = lines[1];

  if (second && normalizeMatchKey(first) === normalizeMatchKey(candidate.store_name || '')) {
    return second;
  }

  return first;
};

const findBestCategoryMatch = (rows: CategoryRow[], rawCategory: string): CategoryRow | null => {
  const needle = normalizeMatchKey(rawCategory);
  if (!needle) return null;

  const exact = rows.find((r) => normalizeMatchKey(r.name) === needle);
  if (exact) return exact;

  const contains = rows.find((r) => {
    const hay = normalizeMatchKey(r.name);
    return hay.includes(needle) || needle.includes(hay);
  });

  return contains || null;
};

const extractWebsiteDomain = (raw: string): string => {
  const value = raw.trim();
  if (!value) return '';

  try {
    const withProtocol = value.startsWith('http://') || value.startsWith('https://') ? value : `https://${value}`;
    const url = new URL(withProtocol);
    const host = url.hostname.toLowerCase();
    return host.startsWith('www.') ? host.slice(4) : host;
  } catch {
    const simplified = value
      .replace(/^https?:\/\//i, '')
      .split('/')[0]
      .toLowerCase();
    return simplified.startsWith('www.') ? simplified.slice(4) : simplified;
  }
};

const checkDuplicateInTable = async (
  tableName: string,
  candidate: Pick<ImportCandidate, 'store_name' | 'location' | 'phone_number' | 'website'>
): Promise<{ id: string | number; reason: string } | null> => {
  const websiteDomain = extractWebsiteDomain(candidate.website || '');
  if (websiteDomain && !NON_UNIQUE_WEBSITE_DOMAINS.has(websiteDomain)) {
    const { data } = await supabaseAdmin
      .from(tableName)
      .select('id')
      .ilike('website', `%${websiteDomain}%`)
      .limit(1);

    if (data && data.length > 0) {
      return { id: data[0].id, reason: 'website' };
    }
  }

  const phone = (candidate.phone_number || '').trim();
  if (phone) {
    const { data } = await supabaseAdmin
      .from(tableName)
      .select('id')
      .eq('phone_number', phone)
      .limit(1);

    if (data && data.length > 0) {
      return { id: data[0].id, reason: 'phone' };
    }
  }

  const name = candidate.store_name.trim();
  const loc = candidate.location.trim();
  if (name && loc) {
    const { data } = await supabaseAdmin
      .from(tableName)
      .select('id')
      .ilike('store_name', name)
      .ilike('location', loc)
      .limit(1);

    if (data && data.length > 0) {
      return { id: data[0].id, reason: 'name_location' };
    }
  }

  return null;
};

const checkDuplicateAcrossAllTables = async (
  candidate: Pick<ImportCandidate, 'store_name' | 'location' | 'phone_number' | 'website'>
): Promise<{ table: string; id: string | number; reason: string } | null> => {
  for (const tableName of BUSINESS_TABLES) {
    const hit = await checkDuplicateInTable(tableName, candidate);
    if (hit) {
      return { table: tableName, ...hit };
    }
  }

  return null;
};

const inferBusinessTypeName = (entry: GoogleMapsEntry): string => {
  const haystack = `${entry.categoryName || ''} ${(entry.categories || []).join(' ')}`.toLowerCase();

  // Simple heuristic for first-pass classification. The user can override it per-row.

  const hospitalityHints = [
    'restaurant',
    'cafe',
    'coffee',
    'bar',
    'pub',
    'takeaway',
    'hotel',
    'b&b',
    'bed',
    'breakfast',
  ];

  if (hospitalityHints.some((h) => haystack.includes(h))) {
    return 'hospitality';
  }

  return 'tradesman';
};

const ImportBusinesses = (): JSX.Element => {
  const [loadingRefs, setLoadingRefs] = useState(false);
  const [importing, setImporting] = useState(false);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [businessTypes, setBusinessTypes] = useState<BusinessTypeRow[]>([]);

  const [tradesProfessions, setTradesProfessions] = useState<CategoryRow[]>([]);
  const [craftProfessions, setCraftProfessions] = useState<CategoryRow[]>([]);
  const [hospitalityCategories, setHospitalityCategories] = useState<CategoryRow[]>([]);
  const [professionalsCategories, setProfessionalsCategories] = useState<CategoryRow[]>([]);

  const [candidates, setCandidates] = useState<ImportCandidate[]>([]);
  const [fileError, setFileError] = useState<string>('');
  const [bulkBusinessTypeId, setBulkBusinessTypeId] = useState<string>('');
  const [addingSuggested, setAddingSuggested] = useState<Record<string, boolean>>({});
  const [importStatusByKey, setImportStatusByKey] = useState<Record<string, ImportStatus>>({});
  const [duplicateHitByKey, setDuplicateHitByKey] = useState<Record<string, DuplicateHit>>({});

  const businessTypeNameById = useMemo(() => {
    const map = new Map<string, string>();
    businessTypes.forEach((bt) => map.set(bt.id, bt.name.toLowerCase()));
    return map;
  }, [businessTypes]);

  const tradeCategories = useMemo(() => {
    const unique = Array.from(
      new Set(
        tradesProfessions
          .map((p) => String(p.category || '').trim())
          .filter(Boolean)
      )
    );

    unique.sort((a, b) => a.localeCompare(b));
    return unique;
  }, [tradesProfessions]);

  useEffect(() => {
    if (candidates.length === 0) return;
    if (businessTypes.length === 0) return;

    setCandidates((prev) =>
      prev.map((c) => {
        const next = { ...c };

        if (!next.business_type_id) {
          const inferredType = inferBusinessTypeName(next.source);
          next.business_type_id =
            businessTypes.find((bt) => bt.name.toLowerCase() === inferredType)?.id || next.business_type_id;
        }

        const btName = businessTypeNameById.get(next.business_type_id) || '';
        if (!btName) return next;

        const primaryCategory = getCandidateRawCategory(next);
        if (!primaryCategory) return next;

        if (btName === 'tradesman' && next.profession_id) {
          const selected = tradesProfessions.find((p) => p.id === next.profession_id);
          const derived = String(selected?.category || '').trim();
          if (derived) next.trade_category = derived;
          return next;
        }

        if (next.profession_id) return next;

        if (btName === 'craftsman') {
          const match = findBestCategoryMatch(craftProfessions, primaryCategory);
          if (match) next.profession_id = match.id;
          return next;
        }

        if (btName === 'hospitality') {
          const match = findBestCategoryMatch(hospitalityCategories, primaryCategory);
          if (match) next.profession_id = match.id;
          return next;
        }

        if (btName.includes('professional')) {
          const match = findBestCategoryMatch(professionalsCategories, primaryCategory);
          if (match) next.profession_id = match.id;
          return next;
        }

        const match = findBestCategoryMatch(tradesProfessions, primaryCategory);
        if (match) {
          if (!next.trade_category) next.trade_category = String(match.category || '').trim();
          next.profession_id = match.id;
        }

        return next;
      })
    );
  }, [
    businessTypes,
    businessTypeNameById,
    candidates.length,
    craftProfessions,
    hospitalityCategories,
    professionalsCategories,
    tradesProfessions,
  ]);

  const addSuggestedProfession = async (candidateKey: string) => {
    const candidate = candidates.find((c) => c.key === candidateKey);
    if (!candidate) return;

    const btName = businessTypeNameById.get(candidate.business_type_id) || '';
    if (!btName) {
      toast({
        title: 'Select business type',
        description: 'Choose a business type before adding a category/profession.',
        variant: 'destructive',
      });
      return;
    }

    const rawName = getCandidateRawCategory(candidate);
    if (!rawName) {
      toast({
        title: 'No category found',
        description: 'This entry does not include a recognizable category to add.',
        variant: 'destructive',
      });
      return;
    }

    let tableName: 'professions' | 'craft_professions' | 'hospitality_categories' | 'professionals_categories' = 'professions';
    let category: string = 'Other';

    if (btName === 'craftsman') {
      tableName = 'craft_professions';
      category = 'Other';
    } else if (btName === 'hospitality') {
      tableName = 'hospitality_categories';
      category = 'Other';
    } else if (btName.includes('professional')) {
      tableName = 'professionals_categories';
      category = 'Other';
    } else {
      tableName = 'professions';
      category = candidate.trade_category || 'Other';
    }

    try {
      setAddingSuggested((prev) => ({ ...prev, [candidateKey]: true }));

      const { data, error } = await supabase.rpc('add_profession', {
        p_table_name: tableName,
        p_name: rawName,
        p_category: category,
      });

      if (error) throw error;
      if (!data) throw new Error('No id returned from add_profession');

      const newRow: CategoryRow = { id: Number(data), name: rawName, category };

      if (tableName === 'craft_professions') {
        setCraftProfessions((prev) => [...prev, newRow]);
      } else if (tableName === 'hospitality_categories') {
        setHospitalityCategories((prev) => [...prev, newRow]);
      } else if (tableName === 'professionals_categories') {
        setProfessionalsCategories((prev) => [...prev, newRow]);
      } else {
        setTradesProfessions((prev) => [...prev, newRow]);
      }

      setCandidates((prev) =>
        prev.map((row) => {
          if (row.key !== candidateKey) return row;
          return {
            ...row,
            profession_id: newRow.id,
            trade_category: tableName === 'professions' ? category : row.trade_category,
          };
        })
      );

      toast({
        title: 'Added',
        description: `Added "${rawName}" to ${tableName}.`,
      });
    } catch (err) {
      console.error('Failed to add suggested profession:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to add category/profession',
        variant: 'destructive',
      });
    } finally {
      setAddingSuggested((prev) => ({ ...prev, [candidateKey]: false }));
    }
  };

  useEffect(() => {
    const loadRefs = async () => {
      setLoadingRefs(true);

      try {
        const [typesRes, tradesRes, craftRes, hospRes, prosRes] = await Promise.all([
          supabaseAdmin.from('business_types').select('id, name').order('name'),
          supabaseAdmin.from('professions').select('id, name, category').order('name'),
          supabaseAdmin.from('craft_professions').select('id, name, category').order('name'),
          supabaseAdmin.from('hospitality_categories').select('id, name, category').order('name'),
          supabaseAdmin.from('professionals_categories').select('id, name, category').order('name'),
        ]);

        if (typesRes.error) throw typesRes.error;
        if (tradesRes.error) throw tradesRes.error;
        if (craftRes.error) throw craftRes.error;
        if (hospRes.error) throw hospRes.error;
        if (prosRes.error) throw prosRes.error;

        setBusinessTypes(typesRes.data || []);
        setTradesProfessions(tradesRes.data || []);
        setCraftProfessions(craftRes.data || []);
        setHospitalityCategories(hospRes.data || []);
        setProfessionalsCategories(prosRes.data || []);
      } catch (err) {
        console.error('Error loading import reference data:', err);
        toast({
          title: 'Error',
          description: 'Failed to load reference data (business types / professions).',
          variant: 'destructive',
        });
      } finally {
        setLoadingRefs(false);
      }
    };

    loadRefs();
  }, []);

  const getProfessionOptions = (candidate: ImportCandidate): CategoryRow[] => {
    const btName = businessTypeNameById.get(candidate.business_type_id) || '';

    if (btName === 'craftsman') {
      return craftProfessions;
    }

    if (btName === 'hospitality') {
      return hospitalityCategories;
    }

    if (btName.includes('professional')) {
      return professionalsCategories;
    }

    const filtered = candidate.trade_category
      ? tradesProfessions.filter((p) => (p.category || '') === candidate.trade_category)
      : [];

    return filtered;
  };

  const parseFile = async (file: File) => {
    setFileError('');
    setCandidates([]);
    setBulkBusinessTypeId('');
    setAddingSuggested({});
    setImportStatusByKey({});
    setDuplicateHitByKey({});

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        throw new Error('JSON must be an array');
      }

      const entries: GoogleMapsEntry[] = parsed;

      const nextCandidates: ImportCandidate[] = entries
        .map((entry) => {
          const inferredType = inferBusinessTypeName(entry);
          const businessTypeId = businessTypes.find((bt) => bt.name.toLowerCase() === inferredType)?.id || '';
          const location = normalizeLocation(entry);
          const storeName = (entry.title || '').trim();

          return {
            key: uuidv4(),
            selected: true,
            source: entry,
            business_type_id: businessTypeId,
            trade_category: '',
            profession_id: null,
            store_name: storeName,
            owner_name: 'Unknown',
            email: '',
            phone_number: (entry.phone || '').trim(),
            location,
            website: (entry.website || '').trim(),
            description: buildDefaultDescription(entry),
            avatar_url: (entry.imageUrl || '').trim(),
          };
        })
        .filter((c) => c.store_name);

      setCandidates(nextCandidates);
      setImportStatusByKey({});
      toast({
        title: 'Loaded',
        description: `Loaded ${nextCandidates.length} entries for review.`,
      });
    } catch (err) {
      console.error('Import JSON parse error:', err);
      const msg = err instanceof Error ? err.message : 'Failed to parse file';
      setFileError(msg);
      setCandidates([]);
    }
  };

  const applyBusinessTypeToAll = () => {
    if (!bulkBusinessTypeId) return;
    setCandidates((prev) =>
      prev.map((c) => ({
        ...c,
        business_type_id: bulkBusinessTypeId,
        trade_category: '',
        profession_id: null,
      }))
    );
  };

  const selectedCount = useMemo(
    () => candidates.filter((c) => c.selected).length,
    [candidates]
  );

  const toggleAll = (nextSelected: boolean) => {
    setCandidates((prev) => prev.map((c) => ({ ...c, selected: nextSelected })));
  };

  const checkDuplicatesSelected = async () => {
    const selected = candidates.filter((c) => c.selected);
    if (selected.length === 0) return;

    setCheckingDuplicates(true);
    setDuplicateHitByKey({});

    let duplicates = 0;
    let blocking = 0;
    let failedChecks = 0;

    try {
      for (const c of selected) {
        const btName = businessTypeNameById.get(c.business_type_id) || '';

        let tableName = 'traders';
        if (btName === 'craftsman') tableName = 'crafties';
        else if (btName === 'hospitality') tableName = 'hospitality';
        else if (btName.includes('professional')) tableName = 'professionals';

        try {
          const duplicate = await withTimeout(
            checkDuplicateAcrossAllTables({
              store_name: c.store_name,
              location: c.location,
              phone_number: c.phone_number,
              website: c.website,
            }),
            20000,
            `Duplicate check (all tables)`
          );

          if (!duplicate) {
            setImportStatusByKey((prev) => {
              const next = { ...prev };
              if (next[c.key]?.message?.startsWith('Duplicate in ')) {
                delete next[c.key];
              }
              return next;
            });
            continue;
          }

          duplicates += 1;

          if (duplicate.table === tableName) {
            const { data: existingRow, error: existingError } = await supabaseAdmin
              .from(tableName)
              .select('id, user_id, verified')
              .eq('id', duplicate.id)
              .maybeSingle();

            if (existingError) throw existingError;

            if (existingRow && !existingRow.user_id && !existingRow.verified) {
              setImportStatusByKey((prev) => ({
                ...prev,
                [c.key]: {
                  state: 'ok',
                  message: `Duplicate in ${duplicate.table} (${duplicate.reason}, id: ${duplicate.id}) - will update existing on import`,
                },
              }));
              continue;
            }
          }

          blocking += 1;
          setDuplicateHitByKey((prev) => ({ ...prev, [c.key]: duplicate }));
          setImportStatusByKey((prev) => ({
            ...prev,
            [c.key]: {
              state: 'skipped',
              message: `Duplicate in ${duplicate.table} (${duplicate.reason}, id: ${duplicate.id})`,
            },
          }));
        } catch (err) {
          failedChecks += 1;
          const msg = err instanceof Error ? err.message : 'Duplicate check failed';
          setImportStatusByKey((prev) => ({
            ...prev,
            [c.key]: {
              state: 'failed',
              message: msg,
            },
          }));
        }
      }

      toast({
        title: 'Duplicate check finished',
        description: `Checked: ${selected.length}, Duplicates: ${duplicates}, Blocking: ${blocking}, Failed: ${failedChecks}`,
        variant: failedChecks > 0 ? 'destructive' : 'default',
      });
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const unselectDuplicates = () => {
    const duplicateKeys = new Set(Object.keys(duplicateHitByKey));
    if (duplicateKeys.size === 0) return;

    let removed = 0;
    setCandidates((prev) =>
      prev.map((c) => {
        if (c.selected && duplicateKeys.has(c.key)) {
          removed += 1;
          return { ...c, selected: false };
        }
        return c;
      })
    );

    toast({
      title: 'Selection updated',
      description: `Unselected ${removed} duplicate entr${removed === 1 ? 'y' : 'ies'}.`,
    });
  };

  const validateCandidate = (c: ImportCandidate): string | null => {
    if (!c.selected) return null;
    if (!c.store_name.trim()) return 'Missing business name';
    if (!c.location.trim()) return 'Missing location';
    if (!c.description.trim()) return 'Missing description';

    const btName = businessTypeNameById.get(c.business_type_id) || '';
    if (!btName) return 'Missing business type';

    if (btName === 'tradesman' && !c.trade_category) return 'Missing trade category';

    if (!c.profession_id) return 'Missing profession/category';

    return null;
  };

  const createPlaceholderEmail = (storeName: string) => {
    const baseSlug = slugify(storeName) || 'unclaimed-business';
    return `${baseSlug}-${uuidv4()}@unclaimed.ballymarket.local`;
  };

  const importSelected = async () => {
    // Import is executed sequentially to keep the logic simple and avoid rate-limits.
    const selected = candidates.filter((c) => c.selected);
    if (selected.length === 0) return;

    setImportStatusByKey({});

    const invalid = selected
      .map((c) => ({ key: c.key, error: validateCandidate(c) }))
      .filter((r) => r.error);

    if (invalid.length > 0) {
      toast({
        title: 'Fix validation errors',
        description: `There are ${invalid.length} selected entries with missing required fields.`,
        variant: 'destructive',
      });
      return;
    }

    setImporting(true);

    let ok = 0;
    let updated = 0;
    let skipped = 0;
    let failed = 0;
    let firstSkipDetail: string | null = null;

    try {
      for (const c of selected) {
        const btName = businessTypeNameById.get(c.business_type_id) || '';

        let tableName = 'traders';
        if (btName === 'craftsman') tableName = 'crafties';
        else if (btName === 'hospitality') tableName = 'hospitality';
        else if (btName.includes('professional')) tableName = 'professionals';

        const rawEmail = c.email.trim();
        const email = rawEmail || createPlaceholderEmail(c.store_name);

        const normalizedPhone = c.phone_number.trim();
        const phoneForInsert = tableName === 'hospitality' || tableName === 'professionals'
          ? (normalizedPhone || null)
          : (normalizedPhone || '');

        const baseData: any = {
          store_name: c.store_name.trim(),
          owner_name: c.owner_name.trim(),
          email,
          phone_number: phoneForInsert,
          location: c.location.trim(),
          description: c.description.trim(),
          profession_id: c.profession_id,
          website: c.website.trim() || null,
          avatar_url: c.avatar_url.trim() || null,
          verified: false,
          mockup: true,
          user_id: null,
          updated_at: new Date().toISOString(),
        };

        if (tableName === 'traders' || tableName === 'hospitality' || tableName === 'professionals') {
          baseData.business_type_id = c.business_type_id;
        }

        try {
          const duplicate = await withTimeout(
            checkDuplicateAcrossAllTables({
              store_name: c.store_name,
              location: c.location,
              phone_number: c.phone_number,
              website: c.website,
            }),
            20000,
            `Duplicate check (all tables)`
          );

          if (duplicate) {
            if (duplicate.table === tableName) {
              const { data: existingRow, error: existingError } = await supabaseAdmin
                .from(tableName)
                .select('id, user_id, verified, mockup')
                .eq('id', duplicate.id)
                .maybeSingle();

              if (existingError) throw existingError;

              if (existingRow && !existingRow.user_id && !existingRow.verified) {
                const updateData: any = {
                  mockup: true,
                  verified: false,
                  user_id: null,
                  store_name: c.store_name.trim(),
                  owner_name: c.owner_name.trim(),
                  phone_number: phoneForInsert,
                  location: c.location.trim(),
                  description: c.description.trim(),
                  profession_id: c.profession_id,
                  updated_at: new Date().toISOString(),
                };

                if (rawEmail) {
                  updateData.email = rawEmail;
                }

                const website = c.website.trim();
                if (website) updateData.website = website;

                const avatar = c.avatar_url.trim();
                if (avatar) updateData.avatar_url = avatar;

                if (tableName === 'traders' || tableName === 'hospitality' || tableName === 'professionals') {
                  updateData.business_type_id = c.business_type_id;
                }

                const { data: updatedRow, error: updateError } = await supabaseAdmin
                  .from(tableName)
                  .update(updateData)
                  .eq('id', duplicate.id)
                  .select()
                  .single();

                if (updateError) throw updateError;

                updated += 1;
                setImportStatusByKey((prev) => ({
                  ...prev,
                  [c.key]: {
                    state: 'ok',
                    message: `Updated existing ${tableName} (id: ${updatedRow?.id ?? duplicate.id})`,
                  },
                }));
                continue;
              }
            }

            skipped += 1;
            if (!firstSkipDetail) {
              firstSkipDetail = `Duplicate in ${duplicate.table} (${duplicate.reason}, id: ${duplicate.id})`;
            }
            setImportStatusByKey((prev) => ({
              ...prev,
              [c.key]: {
                state: 'skipped',
                message: `Duplicate in ${duplicate.table} (${duplicate.reason}, id: ${duplicate.id})`,
              },
            }));
            continue;
          }

          const slugBaseForInsert = slugify(c.store_name) || tableName;
          const inserted = await insertWithSlugRetry<any>(
            tableName,
            baseData,
            slugBaseForInsert,
            65000
          );

          const keywordBusinessType =
            tableName === 'crafties'
              ? 'craftie'
              : tableName === 'hospitality'
                ? 'hospitality'
                : tableName === 'professionals'
                  ? 'professional'
                  : 'trader';

          triggerKeywordGeneration(
            inserted.id,
            keywordBusinessType,
            {
              store_name: inserted.store_name,
              description: inserted.description,
              location: inserted.location,
            }
          ).catch((err) => {
            console.warn('Keyword generation trigger failed for imported business:', err);
          });

          ok += 1;
          setImportStatusByKey((prev) => ({
            ...prev,
            [c.key]: {
              state: 'ok',
              message: `Inserted into ${tableName} (id: ${inserted.id})`,
            },
          }));
        } catch (err) {
          failed += 1;
          console.error('Import failed for row:', { c, err });
          const msg = err instanceof Error ? err.message : 'Import failed';
          setImportStatusByKey((prev) => ({
            ...prev,
            [c.key]: {
              state: 'failed',
              message: msg,
            },
          }));
        }
      }

      toast({
        title: 'Import finished',
        description: `Imported: ${ok}, Updated: ${updated}, Skipped: ${skipped}${firstSkipDetail ? ` (${firstSkipDetail})` : ''}, Failed: ${failed}`,
        variant: failed > 0 ? 'destructive' : 'default',
      });

      if (failed === 0 && skipped === 0) {
        setCandidates([]);
      } else {
        setCandidates((prev) => prev.map((c) => ({ ...c, selected: false })));
      }
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Import Businesses</h1>
          <p className="text-gray-600 mt-1">
            Upload a Google Maps extractor JSON, review entries, set Business Type and Profession, then import as unclaimed businesses.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="import-json">JSON file</Label>
            <Input
              id="import-json"
              type="file"
              accept="application/json,.json"
              disabled={loadingRefs || importing}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                parseFile(file);
              }}
            />
            {fileError && (
              <div className="mt-2 text-sm text-red-600">{fileError}</div>
            )}
            {loadingRefs && (
              <div className="mt-2 text-sm text-gray-500 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading reference data...
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Apply business type to all</Label>
            <div className="flex gap-2">
              <Select
                value={bulkBusinessTypeId || undefined}
                onValueChange={(v) => setBulkBusinessTypeId(v)}
                disabled={loadingRefs || importing}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select business type" />
                </SelectTrigger>
                <SelectContent>
                  {businessTypes.map((bt) => (
                    <SelectItem key={bt.id} value={bt.id}>
                      {bt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                onClick={applyBusinessTypeToAll}
                disabled={!bulkBusinessTypeId || importing}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </div>

      {candidates.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm text-gray-700">
              Selected: <span className="font-semibold">{selectedCount}</span> / {candidates.length}
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => toggleAll(true)}
                disabled={importing || checkingDuplicates}
              >
                <CheckSquare className="w-4 h-4 mr-2" /> Select all
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => toggleAll(false)}
                disabled={importing || checkingDuplicates}
              >
                <XSquare className="w-4 h-4 mr-2" /> Select none
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={checkDuplicatesSelected}
                disabled={importing || checkingDuplicates || selectedCount === 0}
              >
                {checkingDuplicates ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Checking...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" /> Check duplicates
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={unselectDuplicates}
                disabled={importing || checkingDuplicates || Object.keys(duplicateHitByKey).length === 0}
              >
                <Ban className="w-4 h-4 mr-2" /> Unselect duplicates
              </Button>
              <Button
                type="button"
                onClick={importSelected}
                disabled={importing || checkingDuplicates || selectedCount === 0}
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" /> Import selected
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            {candidates.map((c, idx) => {
              const btName = businessTypeNameById.get(c.business_type_id) || '';
              const isTradesman = btName === 'tradesman';
              const professionOptions = getProfessionOptions(c);
              const validationError = validateCandidate(c);
              const importStatus = importStatusByKey[c.key];

              return (
                <div key={c.key} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={c.selected}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setCandidates((prev) =>
                            prev.map((row) =>
                              row.key === c.key ? { ...row, selected: checked } : row
                            )
                          );
                        }}
                        className="mt-1"
                      />
                      <div className="space-y-1">
                        <div className="font-semibold text-gray-900">{idx + 1}. {c.store_name || '(missing name)'}</div>
                        <div className="text-sm text-gray-600">
                          {c.source.categoryName || c.source.categories?.[0] || 'No category'}
                        </div>
                        <div className="text-sm text-gray-600">
                          {c.location || c.source.address || 'No location'}
                        </div>
                        {validationError && c.selected && (
                          <div className="text-sm text-red-600">{validationError}</div>
                        )}
                        {importStatus && (
                          <div
                            className={
                              importStatus.state === 'ok'
                                ? 'text-sm text-green-700'
                                : importStatus.state === 'skipped'
                                  ? 'text-sm text-amber-700'
                                  : 'text-sm text-red-600'
                            }
                          >
                            {importStatus.message}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-400">PlaceId: {c.source.placeId || '-'}</div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label>Location</Label>
                      <Input
                        value={c.location}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCandidates((prev) =>
                            prev.map((row) => (row.key === c.key ? { ...row, location: v } : row))
                          );
                        }}
                        disabled={importing}
                      />
                    </div>

                    <div>
                      <Label>Business Type</Label>
                      <Select
                        value={c.business_type_id || undefined}
                        onValueChange={(v) => {
                          setCandidates((prev) =>
                            prev.map((row) =>
                              row.key === c.key
                                ? {
                                    ...row,
                                    business_type_id: v,
                                    profession_id: (() => {
                                      const nextBtName = businessTypeNameById.get(v) || '';
                                      const primaryCategory = getCandidateRawCategory(row);
                                      if (!nextBtName || !primaryCategory) return null;

                                      if (nextBtName === 'craftsman') {
                                        return findBestCategoryMatch(craftProfessions, primaryCategory)?.id || null;
                                      }
                                      if (nextBtName === 'hospitality') {
                                        return findBestCategoryMatch(hospitalityCategories, primaryCategory)?.id || null;
                                      }
                                      if (nextBtName.includes('professional')) {
                                        return findBestCategoryMatch(professionalsCategories, primaryCategory)?.id || null;
                                      }

                                      const match = findBestCategoryMatch(tradesProfessions, primaryCategory);
                                      return match?.id || null;
                                    })(),
                                    trade_category: (() => {
                                      const nextBtName = businessTypeNameById.get(v) || '';
                                      const primaryCategory = getCandidateRawCategory(row);
                                      if (nextBtName !== 'tradesman' || !primaryCategory) return '';
                                      return String(findBestCategoryMatch(tradesProfessions, primaryCategory)?.category || '').trim();
                                    })(),
                                  }
                                : row
                            )
                          );
                        }}
                        disabled={importing}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select business type" />
                        </SelectTrigger>
                        <SelectContent>
                          {businessTypes.map((bt) => (
                            <SelectItem key={bt.id} value={bt.id}>
                              {bt.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {isTradesman && (
                      <div>
                        <Label>Trade Category</Label>
                        <Select
                          value={c.trade_category || undefined}
                          onValueChange={(v) => {
                            setCandidates((prev) =>
                              prev.map((row) =>
                                row.key === c.key
                                  ? {
                                      ...row,
                                      trade_category: v,
                                      profession_id: (() => {
                                        const primaryCategory = getCandidateRawCategory(row);
                                        if (!primaryCategory) return null;
                                        const scoped = tradesProfessions.filter((p) => (p.category || '') === v);
                                        return findBestCategoryMatch(scoped, primaryCategory)?.id || null;
                                      })(),
                                    }
                                  : row
                              )
                            );
                          }}
                          disabled={importing}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select trade category" />
                          </SelectTrigger>
                          <SelectContent>
                            {c.trade_category && !tradeCategories.includes(c.trade_category) && (
                              <SelectItem key={`selected-${c.trade_category}`} value={c.trade_category}>
                                {c.trade_category}
                              </SelectItem>
                            )}
                            {tradeCategories.map((cat) => (
                              <SelectItem key={cat} value={cat}>
                                {cat}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div>
                      <Label>Profession / Category</Label>
                      <Select
                        value={c.profession_id ? String(c.profession_id) : undefined}
                        onValueChange={(v) => {
                          const id = Number(v);
                          setCandidates((prev) =>
                            prev.map((row) =>
                              row.key === c.key
                                ? {
                                    ...row,
                                    profession_id: isNaN(id) ? null : id,
                                    trade_category:
                                      btName === 'tradesman'
                                        ? String(tradesProfessions.find((p) => p.id === id)?.category || row.trade_category).trim()
                                        : row.trade_category,
                                  }
                                : row
                            )
                          );
                        }}
                        disabled={importing || professionOptions.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={professionOptions.length === 0 ? 'Select business type first' : 'Select profession'} />
                        </SelectTrigger>
                        <SelectContent>
                          {professionOptions.map((p) => (
                            <SelectItem key={p.id} value={String(p.id)}>
                              {p.name}{p.category ? ` (${p.category})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(() => {
                        const rawCategory = getCandidateRawCategory(c);
                        if (!rawCategory) return null;
                        if (c.profession_id) return null;
                        if (!btName) return null;

                        const optionsForMatch = btName === 'tradesman'
                          ? (c.trade_category ? professionOptions : tradesProfessions)
                          : professionOptions;

                        const match = findBestCategoryMatch(optionsForMatch, rawCategory);
                        if (match) return null;

                        return (
                          <div className="text-xs text-amber-700 mt-1 flex items-center justify-between gap-2">
                            <div>
                              Not found: "{rawCategory}".
                            </div>
                            <button
                              type="button"
                              className="text-blue-600 hover:underline disabled:opacity-50"
                              onClick={() => addSuggestedProfession(c.key)}
                              disabled={importing || Boolean(addingSuggested[c.key])}
                            >
                              {addingSuggested[c.key] ? 'Adding...' : 'Add'}
                            </button>
                          </div>
                        );
                      })()}
                    </div>

                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={c.phone_number}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCandidates((prev) =>
                            prev.map((row) => (row.key === c.key ? { ...row, phone_number: v } : row))
                          );
                        }}
                        disabled={importing}
                      />
                    </div>

                    <div>
                      <Label>Website</Label>
                      <Input
                        value={c.website}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCandidates((prev) =>
                            prev.map((row) => (row.key === c.key ? { ...row, website: v } : row))
                          );
                        }}
                        disabled={importing}
                      />
                    </div>

                    <div>
                      <Label>Owner name</Label>
                      <Input
                        value={c.owner_name}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCandidates((prev) =>
                            prev.map((row) => (row.key === c.key ? { ...row, owner_name: v } : row))
                          );
                        }}
                        disabled={importing}
                      />
                    </div>

                    <div>
                      <Label>Email (optional)</Label>
                      <Input
                        value={c.email}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCandidates((prev) =>
                            prev.map((row) => (row.key === c.key ? { ...row, email: v } : row))
                          );
                        }}
                        disabled={importing}
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        Leave empty to keep email blank in the form. During import we will generate a placeholder email because the DB column is NOT NULL.
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <Label>Description</Label>
                      <Textarea
                        value={c.description}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCandidates((prev) =>
                            prev.map((row) => (row.key === c.key ? { ...row, description: v } : row))
                          );
                        }}
                        rows={4}
                        disabled={importing}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label>Avatar URL</Label>
                      <Input
                        value={c.avatar_url}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCandidates((prev) =>
                            prev.map((row) => (row.key === c.key ? { ...row, avatar_url: v } : row))
                          );
                        }}
                        disabled={importing}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportBusinesses;
